import { VatPercentageByBuildingDto, VatVarianceDto } from '@client/api';
import i18n from 'i18next';

const round2 = (v: number) => Math.round(v * 100) / 100;

const variancePp = (variance?: VatVarianceDto): number | string | null => {
  if (!variance) return '-';
  if (variance.status === 'New') return i18n.t('vatPercentage.new', { ns: 'reporting' });
  if (variance.status === 'N/A' || variance.status === 'NoData') return i18n.t('vatPercentage.notAvailable', { ns: 'reporting' });
  if (variance.absolutePp == null) return '-';
  return round2(variance.absolutePp);
};

const variancePercent = (variance?: VatVarianceDto): number | string | null => {
  if (!variance) return '-';
  if (variance.status === 'New') return i18n.t('vatPercentage.new', { ns: 'reporting' });
  if (variance.status === 'N/A' || variance.status === 'NoData') return i18n.t('vatPercentage.notAvailable', { ns: 'reporting' });
  if (variance.status === 'NoPrior') return i18n.t('vatPercentage.noPrior', { ns: 'reporting' });
  if (variance.relativePercent == null) return '-';
  return round2(variance.relativePercent);
};

const cleanVat = (v: number | null | undefined): string =>
  v != null ? `${round2(v)}%` : '-';

export const transformToExcelFormat = (
  data: VatPercentageByBuildingDto[],
  selectedRows: Set<string>,
  baseYear: number,
  comparisonYear: number,
) => {
  const rows: Record<string, any>[] = [];
  if (data.length === 0) return [{ [i18n.t('noData', { ns: 'reporting' })]: null }];

  data.forEach((item) => {
    if (selectedRows.has(item.buildingId!) || selectedRows.size === 0) {
      rows.push({
        [i18n.t('vatPercentage.building', { ns: 'reporting' })]: item.buildingName,
        [i18n.t('vatPercentage.tenants', { ns: 'reporting' })]: item.numberOfTenants ?? 0,
        [i18n.t('vatPercentage.vatPercentageYear', { year: baseYear, ns: 'reporting' })]: 
          cleanVat(item.currentPeriodAverageVat),
        [i18n.t('vatPercentage.vatPercentageYear', { year: comparisonYear, ns: 'reporting' })]: 
          cleanVat(item.comparisonPeriodAverageVat),
        [i18n.t('vatPercentage.agreement', { ns: 'reporting' })]: '-',
        [i18n.t('vatPercentage.updated', { ns: 'reporting' })]: '-',
        [i18n.t('vatPercentage.trendPp', { ns: 'reporting' })]: variancePp(item.variance),
        [i18n.t('vatPercentage.trendPercent', { ns: 'reporting' })]: variancePercent(item.variance),
      });
      item.tenants?.forEach((tenant) => {
        rows.push({
          [i18n.t('vatPercentage.building', { ns: 'reporting' })]: null,
          [i18n.t('vatPercentage.tenants', { ns: 'reporting' })]: tenant.tenantName,
          [i18n.t('vatPercentage.vatPercentageYear', { year: baseYear, ns: 'reporting' })]: 
            cleanVat(tenant.currentPeriodVatPercentage),
          [i18n.t('vatPercentage.vatPercentageYear', { year: comparisonYear, ns: 'reporting' })]: 
            cleanVat(tenant.comparisonPeriodVatPercentage),
          [i18n.t('vatPercentage.agreement', { ns: 'reporting' })]: tenant.agreementName,
          [i18n.t('vatPercentage.updated', { ns: 'reporting' })]: tenant.lastUpdated ? new Date(tenant.lastUpdated).toLocaleDateString() : '-',
          [i18n.t('vatPercentage.trendPp', { ns: 'reporting' })]: variancePp(tenant.variance),
          [i18n.t('vatPercentage.trendPercent', { ns: 'reporting' })]: variancePercent(tenant.variance),
        });
      });
    }
  });
  return rows;
};
